import React from 'react'

import { REVEAL_EVENT } from 'browser/components/atomic-elements/atoms/section/section'
import {
  FormFocusTargetContext,
  IFocusTarget,
  IFormFocusTargetContext,
  IFormFocusHandle,
  FormFocusHandleContext,
  addRegistration,
  pickFocusTarget,
  removeRegistration,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import type { PulseIntent } from 'shared-libs/components/ui2/behavior/pulse-highlight'

interface IFormFocusTargetProviderProps {
  children: React.ReactNode
}

const FormFocusTargetContextProvider = FormFocusTargetContext.Provider as React.ComponentType<{
  value: IFormFocusTargetContext
  children?: React.ReactNode
}>

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}

function isAlive(target: IFocusTarget<HTMLElement>): boolean {
  const node = target.getRef()
  return !!node && node.isConnected
}

/**
 * Web provider of the form focus-target context. Fields register by path into
 * ref-held state (several may share a path) so register/unregister never
 * re-renders, and this owns the timing for `focusFirstError()` / `focusPath()`
 * so callers just await. Errored candidates sort by (section rank, viewport
 * top); section rank keeps cross-section order deterministic when a hidden
 * section reports zero bounds under `display: none`.
 */
export const FormFocusTargetProvider: React.FC<IFormFocusTargetProviderProps> = ({ children }) => {
  const targetsRef = React.useRef<Map<string, Array<IFocusTarget<HTMLElement>>>>(new Map())

  const contextValue = React.useMemo<IFormFocusTargetContext<HTMLElement>>(() => {
    const entriesFor = (path: string): Array<IFocusTarget<HTMLElement>> =>
      targetsRef.current.get(path) ?? []

    const completeFocus = async (
      target: IFocusTarget<HTMLElement> | undefined,
      intent: PulseIntent = 'focus',
      shouldPulse: boolean = true,
    ): Promise<boolean> => {
      if (!target) return false
      if (intent === 'error' && !target.hasError?.()) return false
      const node = target.getRef()
      if (!node) return false
      node.dispatchEvent(new CustomEvent(REVEAL_EVENT, { bubbles: true }))
      await nextFrame()
      node.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const focusNode = isFocusable(node) ? node : node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
      focusNode?.focus({ preventScroll: true })
      if (shouldPulse) target.pulse?.(target.hasError?.() ? 'error' : intent)
      return true
    }

    const sectionRanks = (): Map<string | undefined, number> => {
      const ranks = new Map<string | undefined, number>()
      let next = 0
      targetsRef.current.forEach((list) => {
        list.forEach((target) => {
          const id = target.sectionId
          if (ranks.has(id)) return
          ranks.set(id, id ? ++next : 0)
        })
      })
      return ranks
    }

    const rowsInLayoutOrder = (): Array<{ path: string; target: IFocusTarget<HTMLElement> }> => {
      const ranks = sectionRanks()
      const rows: Array<{ path: string; target: IFocusTarget<HTMLElement>; section: number; top: number }> = []
      targetsRef.current.forEach((list, path) => {
        list.forEach((target) => {
          const node = target.getRef()
          if (!node || !node.isConnected) return
          rows.push({
            path,
            target,
            section: ranks.get(target.sectionId) ?? 0,
            top: node.getBoundingClientRect().top,
          })
        })
      })
      rows.sort((a, b) => (a.section - b.section) || (a.top - b.top))
      return rows.map((r) => ({ path: r.path, target: r.target }))
    }

    const focusTargetWithIntent = async (
      path: string,
      intent: PulseIntent,
      shouldPulse: boolean = true,
      target?: IFocusTarget<HTMLElement>,
    ): Promise<boolean> => {
      await nextFrame()
      const resolved = target ?? pickFocusTarget(entriesFor(path), { isAlive })
      if (!resolved) return false
      resolved.revealSection?.()
      await nextFrame()
      return await completeFocus(resolved, intent, shouldPulse)
    }

    const value: IFormFocusTargetContext<HTMLElement> = {
      register: (path, target) => {
        targetsRef.current.set(path, addRegistration(targetsRef.current.get(path), target))
      },
      unregister: (path, registrationId) => {
        const next = removeRegistration(targetsRef.current.get(path), registrationId)
        if (next.length > 0) targetsRef.current.set(path, next)
        else targetsRef.current.delete(path)
      },
      getRef: (path) => pickFocusTarget(entriesFor(path), { isAlive })?.getRef() ?? null,
      getOrderedPaths: () => Array.from(new Set(rowsInLayoutOrder().map((row) => row.path))),
      focusPath: (path, options) => focusTargetWithIntent(path, 'focus', options?.pulse ?? true),
      focusFirstError: async (scope) => {
        await nextFrame()
        for (const { path, target } of rowsInLayoutOrder()) {
          if (scope?.sectionId && target.sectionId !== scope.sectionId) continue
          if (!target.hasError?.()) continue
          if (await focusTargetWithIntent(path, 'error', true, target)) return path
        }
        return undefined
      },
    }
    return value
  }, [])

  const handle = React.useMemo<IFormFocusHandle>(() => ({
    focusFirstError: (scope) => contextValue.focusFirstError(scope),
    focusPath: (path, options) => contextValue.focusPath(path, options),
    getOrderedPaths: () => contextValue.getOrderedPaths(),
    getTarget: (path) =>
      pickFocusTarget(targetsRef.current.get(path), { isAlive }) as IFocusTarget | undefined,
  }), [contextValue])
  const handleSetter = React.useContext(FormFocusHandleContext)
  React.useEffect(() => {
    if (!handleSetter) return
    handleSetter(handle)
    return () => handleSetter(null)
  }, [handleSetter, handle])

  return (
    <FormFocusTargetContextProvider value={contextValue as IFormFocusTargetContext}>
      {children}
    </FormFocusTargetContextProvider>
  )
}

const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'

function isFocusable(node: HTMLElement): boolean {
  const tag = node.tagName
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return true
  return node.hasAttribute('tabindex')
}
